import React from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocation, useHistory } from 'react-router';

import { bindActionCreators } from 'redux';

import loadable from '@loadable/component';

import {loadableDelay} from '@common/react/loadable/loadableSettings';
import Autocomplete from '@common/react/components/Forms/Autocomplete/Autocomplete';
import { Nullable } from '@common/typescript/objects/Nullable';
import { WithId } from '@common/typescript/objects/WithId';
import { parseQuery } from '@common/typescript/utils/url';
import { BaseParams } from '@common/react/objects/BaseParams';
import Logout from '@common/react/components/UI/Logout/Logout';
import {getUserName} from '@common/react/utils/utils';
import ImageLazy from '@common/react/components/UI/ImageLazy/ImageLazy';
import FiltersComponent from '@common/react/components/UI/FiltersComponent/FiltersPropsHandler';

import { GlobalPayer } from '@commonTuna/react/objects/GlobalPayer';
import { DoctorSpecialty } from '@commonTuna/react/objects/doctorSpecialty';
import { GlobalProcedure } from '@commonTuna/react/objects/GlobalProcedure';
import Avatar from '@commonTuna/react/components/UI/Avatar/Avatar';
import { LogoLoaderPage } from '@commonTuna/react/components/UI/LogoLoader/LogoLoader';

import { Doctor } from '@app/objects/Doctor';
import { Location } from '@app/objects/Location';
import { UserRole } from '@app/objects/User';
import { ApplicationState } from '@app/store';
import { actionCreators } from '@app/store/HeaderSearch';
import { request } from '@app/components/Api';
import BurgerButton from '@app/components/UI/BurgerButton/BurgerButton';
import DashboardTopAlert from '@app/components/UI/DashboardTopAlert/DashboardTopAlert';
import ImageWithSkeleton from '@app/components/UI/ImageWithSkeleton/ImageWithSkeleton';
import { SearchFilterState } from '@app/store/SearchFilter';

import '@app/scss/pages/headerSearch.scss';
import ShoppingCartDropdown from '@app/components/UI/ShoppingCartDropdown/ShoppingCartDropdown';

const params = {
	fallback: <LogoLoaderPage/>
};

const HeaderFilters = loadable(() =>
	loadableDelay(import(/* webpackChunkName: "HeaderFilters" */ './HeaderFilters')), params);
const ThemeSwitch = loadable(() =>
	loadableDelay(import(/* webpackChunkName: "ThemeSwitch" */ '@app/components/UI/ThemeSwitch')), params);

interface SearchResultRow extends WithId {
	doctor: Nullable<Doctor>;
	specialties: Nullable<DoctorSpecialty>;
	globalProcedure: Nullable<GlobalProcedure>;
}

interface IValues extends SearchFilterState {
	text: string;
}

interface IProps {
}

interface CheckBoxItem {
	label: string;
	value: number;
}

const getPopupContainer = node => node.closest('.app-container') || document.body;

const searchTitle = (value) => {
	let result: { node: React.ReactNode, title: string } = {node: <></>, title: ''};
	if (value.doctor !== null) {
		result = {
			node: <>
				<i className="fa fa-user-md mr10" aria-hidden="true" />
				{value.doctor.nameEn}
			</>,
			title: `Doctor: ${value.doctor.nameEn}`
		};
	} else if (value.globalProcedure !== null) {
		result = {
			node: <>
				<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="procedures" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" className="fa-procedures mr10"><path fill="currentColor" d="M528 224H272c-8.8 0-16 7.2-16 16v144H64V144c0-8.8-7.2-16-16-16H16c-8.8 0-16 7.2-16 16v352c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-48h512v48c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V336c0-61.9-50.1-112-112-112zM136 96h126.1l27.6 55.2c5.9 11.8 22.7 11.8 28.6 0L368 51.8 390.1 96H512c8.8 0 16-7.2 16-16s-7.2-16-16-16H409.9L382.3 8.8C376.4-3 359.6-3 353.7 8.8L304 108.2l-19.9-39.8c-1.4-2.7-4.1-4.4-7.2-4.4H136c-4.4 0-8 3.6-8 8v16c0 4.4 3.6 8 8 8zm24 256c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64z" /></svg>
				{value.globalProcedure.name}
			</>,
			title: `Procedure: ${value.globalProcedure.name}`
		};
	} else if (value.specialty !== null) {
		result = {
			node: <>
				<i className="fa fa-briefcase mr10" aria-hidden="true" />
				{value.specialty.name}
			</>,
			title: `Specialty: ${value.specialty.name}`
		};
	}
	return result;
};

const filtersForClear = [
	'languageIds',
	'globalProcedureIds',
	'specialtyIds',
	'from',
	'certificateIds',
	'gender'
];

const HeaderSearch: React.FC<IProps> = ({
}) => {
	const history = useHistory();
	const location = useLocation();
	const { user, transmuted } = useSelector((state: ApplicationState) => state.login, shallowEqual);
	const state = useSelector((state: ApplicationState) => state.header, shallowEqual);
	const searchFilterData = useSelector((state: ApplicationState) => state.searchFilterData, shallowEqual);
	const dispatch = useDispatch();
	const { setState } = bindActionCreators(actionCreators, dispatch);

	const userName = user ? (user.firstName || user.lastName ? getUserName(user) : user.email) : '';
	const [languages, setLanguages] = React.useState<CheckBoxItem[]>([]);
	const [certificates, setCertificates] = React.useState<CheckBoxItem[]>([]);
	const [keys, setKeys] = React.useState<any>({ key: 'initial' });

	const transmuteBack = () => {
		if (transmuted) {
			request('transmutation', {
				id: 0,
				transmutation: !transmuted
			}).then(() => window.location.reload());
		}
	};

	const [isOpen, setIsOpen] = React.useState<boolean>(false);

	const toggle = () => setIsOpen(!isOpen);

	const [values, setValues] = React.useState<IValues>({
		locationName: '',
		globalPayerName: '',
		text: '',
		languages: [],
		certificates: [],
	});

	const handleUrl = (arr: { name: string, value: any}[]) => {
		const searchObj = parseQuery(location.search);

		arr.forEach(({ name, value }) => {
			searchObj[name] = value instanceof Array ? `[${value}]` : `${value}`;
		});

		const emptyValues = ['', 'null', 'undefined', '-1', '[]'];

		const search = Object.keys(searchObj)
			.filter(k => emptyValues.indexOf(searchObj[k]) === -1)
			.map(k => `${k}=${searchObj[k]}`).join('&');
		if (location.pathname !== '/doctors') {
			history.push(`/doctors?${search}`);
		} else {
			history.replace(`${location.pathname.replace(/\/\d+/, '/1')}?${search}`);
		}
	};

	const onSelectSearch = (value, option) => {
		const obj = option.props.item;
		if (obj.doctor !== null) {
			setState({
				...state,
				doctorId: obj.doctor.id,
				specialtyId: undefined,
				globalProcedureId: undefined
			});
			handleUrl([{ name: 'doctorId', value: obj.doctor.id },
				{ name: 'specialtyId', value: - 1},
				{ name: 'globalProcedureId', value: -1 },
				{ name: 'text', value: '' }]);
		} else if (obj.specialty !== null && obj.specialty !== undefined) {
			setState({
				...state,
				doctorId: undefined,
				specialtyId: obj.specialty.id,
				globalProcedureId: undefined
			});
			handleUrl([{ name: 'doctorId', value: -1 },
				{ name: 'specialtyId', value: obj.specialty.id },
				{ name: 'globalProcedureId', value: -1 },
				{ name: 'text', value: '' }]);
		} else if (obj.globalProcedure !== null) {
			setState({
				...state,
				doctorId: undefined,
				specialtyId: undefined,
				globalProcedureId: obj.globalProcedure.id
			});
			handleUrl([{ name: 'doctorId', value: -1 },
				{ name: 'specialtyId', value: -1 },
				{ name: 'globalProcedureId', value: obj.globalProcedure.id },
				{ name: 'text', value: '' }]);
		}
	};

	const getSearchParamsFromUrl = () => {
		const searchObj: BaseParams = parseQuery(location.search);

		for (const key of Object.keys(searchObj)) {
			const value: string = searchObj[key];

			if (value === 'true') {
				searchObj[key] = true;
			} else if (value === 'false') {
				searchObj[key] = false;
			} else if (value[0] === '[' && value[value.length - 1] === ']') {
				searchObj[key] = value.slice(1, value.length - 1).split(',');
			}
		}

		return searchObj;
	};

	const onChange: any = (name, value) => {
		handleUrl([{name, value}]);
		setState({ ...state, [name]: value });
	};

	const handleSearchFilterResult = (searchObj: BaseParams, result: SearchFilterState) => {
		const text = searchObj.text;
		if (!text) {
			if (result.doctorName) {
				handleUrl([
					{ name: 'specialtyId', value: -1 },
					{ name: 'globalProcedureId', value: -1 },
					{ name: 'text', value: '' }
				]);
			} else if (result.globalProcedureName) {
				handleUrl([
					{ name: 'doctorId', value: -1 },
					{ name: 'specialtyId', value: -1 },
					{ name: 'text', value: '' }
				]);
			} else if (result.specialtyName) {
				handleUrl([
					{ name: 'doctorId', value: -1 },
					{ name: 'globalProcedureId', value: -1 },
					{ name: 'text', value: '' }
				]);
			}
		}

		setLanguages(result.languages?.map(value => ({ label: value.name, value: value.id })));
		setCertificates(result.certificates?.map(value => ({ label: value.name, value: value.id })));

		setKeys(prev => ({
			...prev,
			globalProcedureIds: Math.random(),
			specialtyIds: Math.random()
		}));
		setState(searchObj);
	};
	
	React.useLayoutEffect(() => {
		let searchObj: BaseParams = {};

		if (location.pathname === '/doctors') {
			searchObj = getSearchParamsFromUrl();
		} else {
			searchObj = {
				doctorId: -1,
				specialtyId: -1,
				globalProcedureId: -1,
			};
		}

		if (searchObj.text) {
			handleUrl([
				{ name: 'doctorId', value: -1 },
				{ name: 'specialtyId', value: -1 },
				{ name: 'globalProcedureId', value: -1 },
				{ name: 'text', value: searchObj.text }
			]);
		}

		handleSearchFilterResult(searchObj, searchFilterData);
	}, []);

	React.useLayoutEffect(() => {
		let text = state.text;
		if (!text) {
			if (searchFilterData.doctorName) {
				text = `Doctor: ${searchFilterData.doctorName}`;
			} else if (searchFilterData.globalProcedureName) {
				text = `Procedure: ${searchFilterData.globalProcedureName}`;
			} else if (searchFilterData.specialtyName) {
				text = `Specialty: ${searchFilterData.specialtyName}`;
			}
		}

		setValues(prev => ({
			...prev,
			...searchFilterData,
			text: text || ''
		}));
	}, [searchFilterData]);

	return (
		<div className="search-header">
			<div className="search-page search-page-content pull-to-end">
				{user && !user?.emailConfirmed && <DashboardTopAlert objectId={user.id} />}
				<div className="logo">
					<Link to="/">
						<ImageLazy src="/amp-assets/images/logo.png" alt="Northern Tuna Logo" height="72px" width="150px" />
					</Link>
				</div>
				<div className={`row search ${isOpen ? 'search_open' : 'search_close'}`}>
					<div className="form-group col-sm-4">
						<Autocomplete<SearchResultRow>
							type="search"
							loadOnFocus
							renderOption={(option) => {
								const { node, title } = searchTitle(option);

								return {
									label: node,
									title,
									item: option,
									key: option.id,
									value: title
								};
							}}
							value={values.text || ''}
							onSelect={onSelectSearch}
							onChange={(value = '') => {
								value === '' && setState({
									...state,
									text: value,
									doctorId: undefined,
									specialtyId: undefined,
									globalProcedureId: undefined
								});
								handleUrl([
									{ name: 'doctorId', value: -1 },
									{ name: 'specialtyId', value: -1 },
									{ name: 'globalProcedureId', value: -1 },
									{ name: 'text', value }
								]);
							}}
							antdProps={{
								style: { width: '100%' },
								defaultActiveFirstOption: false,
								placeholder: 'Doctor / Procedure / Speciality'
							}}
							params={{ count: 5 }}
						/>
					</div>
					<div className="form-group col-sm-4">
						<Autocomplete<Location>
							type="locationList"
							renderTitle={(item: Location) => `${item.company ? `${item.company.name} - ` : ''}${item.nameEn}`}
							loadOnFocus
							onSelect={(value, option) => {
								setState({
									...state,
									locationId: +value,
								});
								handleUrl([{ name: 'locationId', value: +value }]);
							}}
							onChange={(value = '') => {
								setState({ ...state, locationId: undefined });

								handleUrl([{ name: 'locationId', value: - 1 }]);
							}}
							renderOption={(option) => {
								return {
									key: option.id,
									title: `${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`,
									value: `${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`,
									item: option,
									label: <>
										{option.avatar &&
											<div className="option-img__container">
												<ImageWithSkeleton className="option-img" src={`/remote/${option.avatar}`} />
											</div>
										}
										{`${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`}
									</>,
								};
							}}
							value={values.locationName || ''}
							antdProps={{ style: { width: '100%' }, placeholder: 'Location...' }}
						/>
					</div>
					<div className="form-group col-sm-4">
						<Autocomplete<GlobalPayer>
							type="globalPayerList"
							renderTitle="name"
							loadOnFocus
							onSelect={(value, option) => {
								setState({
									...state,
									globalPayerId: +value,
								});
								handleUrl([{ name: 'globalPayerId', value: +value }]);
							}}
							onChange={(value = '') => {
								setState({ ...state, globalPayerId: undefined });
								handleUrl([{ name: 'globalPayerId', value: -1 }]);
							}}
							renderOption={(option) => {
								return {
									key: option.id,
									title: option.name,
									value: option.name,
									item: option,
									label: <>
										{option.avatar &&
											<div className="option-img__container">
												<ImageWithSkeleton className="option-img" src={`/remote/${option.avatar}`} />
											</div>
										}
										{option.name}
									</>,
								};
							}}
							value={values.globalPayerName || ''}
							antdProps={{ style: { width: '100%' }, placeholder: 'Insurance...' }}
						/>
					</div>
					<div className="col-sm-12 more-filters__container">
						<div className="pull-left">
							<FiltersComponent
								additionalParams={state}
								getPopupContainer={getPopupContainer}
								filtersClean={() => {
									const params = {};
									Object.values(filtersForClear).forEach(key => params[key] = undefined);
									handleUrl(Object.keys(params).map(key => ({ name: key, value: params[key] })));
									setState({
										...state,
										...params
									});
									setValues({
										...values,
										globalProcedures: undefined,
										specialties: undefined
									});
									setKeys(prev => ({
										...prev,
										globalProcedureIds: Math.random(),
										specialtyIds: Math.random()
									}));
								}}
								triggerNode={<a className="more-filters__trigger">
									More Filters
								</a>}
								tooltipValues={<div>
									<div className="filters-container" style={{margin: 0}} data-param={filtersForClear}>
										<HeaderFilters
											state={state}
											onChange={onChange}
											keys={keys}
											values={values}
											certificates={certificates}
											languages={languages}
											getPopupContainer={getPopupContainer}
										/>
									</div>
								</div>}
							/>
						</div>
					</div>
				</div>
				<ShoppingCartDropdown />
				{!!user ?
					<div className="search-header-user-block">
						<ul className={`search-header-user-block__icons ${transmuted ? 'short' : ''}`}>
							<li className="search-icon">
								<Link to="/"><i className="fa fa-search" title="search"/></Link>
							</li>
							{/*
							<li>
								<Link to="/profile"><i className="fa fa-user" title="Profile"/>
									{user && user.unviewedMessagesCount > 0 && <div className="badge search-header-user-block__badge">{user.unviewedMessagesCount}</div>}
								</Link>
							</li>
							*/}
							<li>
								<Link to="/chats"><i className="fa fa-comments" title="Chats"/>
									{user && user.unviewedMessagesCount > 0 && <div className="badge search-header-user-block__badge">{user.unviewedMessagesCount}</div>}
								</Link>
							</li>
							<li>
								<Link to="/notifications"><i className="fa fa-bell" title="Notifications" />
									{user.unviewedNotificationsCount > 0 && <div className="badge search-header-user-block__badge">{user.unviewedNotificationsCount}</div>}
								</Link>
							</li>
							<li>
								<Logout
									render={logout => <i onClick={logout} className="fa fa-power-off" title="Logout"/>}
									clearState={true}
								/>
							</li>
							{transmuted &&
								<li>
									<i className="fa fa-arrow-left" title="Login back" onClick={transmuteBack} />
								</li>
							}
							<li className="avatar">
								<Link to="/dashboard">
									<div className="search-header-avatar" title={userName}>
										<Avatar src={user.avatar} original={user.originalAvatar} color={user.color} firstName={user.firstName} lastName={user.lastName} />
									</div>
								</Link>
							</li>
							{user && user.role === UserRole.Admin && <li className="search-header-user-block__switch">
								<ThemeSwitch/>
							</li>}
						</ul>
					</div> :
					<>
						<div className="header-link">
							<h3><Link to="/login">Sign In</Link></h3>
						</div>
						<div className="header-link">
							<h3><Link to="/registration">Sign Up</Link></h3>
						</div>
					</>
				}
				<BurgerButton isOpen={isOpen} onClick={toggle}/>
			</div>
		</div>
	);
};

export default HeaderSearch;
